import React, { useState, useEffect, useCallback } from 'react';
import Navigation from './components/Navigation';
import HomePage from './components/HomePage';
import ReviewsPage from './components/ReviewsPage';
import GalleryPage from './components/GalleryPage';
import ProjectsPage from './components/ProjectsPage';
import AdminModal from './components/AdminModal';
import ReviewModal from './components/ReviewModal';
import ProjectModal from './components/ProjectModal';
import Footer from './components/Footer';
import { LoadingNotice, LoadError, isPending } from './components/LoadState';
import { toTechList } from './utils/links';
import { observeAuthState, signOutUser } from './firebase/auth';
import {
  getReviews,
  addReview,
  updateReview,
  deleteReview,
  addSubscriber,
  getSubscribers,
  getProjects,
  addProject,
  updateProject,
  deleteProject,
  saveImageData,
  getImages,
  deleteImage
} from './firebase/firestore';
import './styles/globals.css';

// Simple Error Boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('❌ Component Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div role="alert" className="page py-12">
          <div className="max-w-xl rounded-xl bg-red-500/10 p-6 text-red-200 ring-1 ring-inset ring-red-400/30">
            <h2 className="text-lg font-semibold text-slate-50">This page couldn't be displayed</h2>
            <p className="mt-2">Something went wrong while showing this section. Reloading usually fixes it.</p>
            <button type="button" onClick={() => window.location.reload()} className="btn-secondary mt-4 px-4 py-2">
              Reload page
            </button>
            {process.env.NODE_ENV !== 'production' && (
              <pre className="mt-4 overflow-auto rounded-xl bg-slate-950 p-3 font-mono text-xs">
                {this.state.error?.toString()}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// After this long, a still-pending load offers a retry instead of spinning silently.
const SLOW_LOAD_MS = 10000;

// Fetch one collection, tracking its own status. A refresh of data that is
// already on screen never blanks it: failures keep the old data visible.
const runLoad = async ({ label, fetcher, pick, setData, setStatus }) => {
  setStatus((s) => (s === 'ready' ? s : 'loading'));
  const slowTimer = setTimeout(
    () => setStatus((s) => (s === 'loading' ? 'slow' : s)),
    SLOW_LOAD_MS
  );
  try {
    const result = await fetcher();
    if (!result.success) throw new Error(result.error || 'Unknown error');
    const items = pick(result) || [];
    // Offline, Firestore answers from its local cache. Cached items are worth
    // showing, but an empty cache means "unknown", not "nothing published".
    if (result.fromCache && items.length === 0) {
      throw new Error('Offline: no cached data');
    }
    setData(items);
    setStatus('ready');
  } catch (err) {
    console.error(`❌ Error loading ${label}:`, err);
    setStatus((s) => (s === 'ready' ? s : 'error'));
  } finally {
    clearTimeout(slowTimer);
  }
};

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [projects, setProjects] = useState([]);
  const [images, setImages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [newReview, setNewReview] = useState({
    title: '',
    rating: 5,
    content: '',
    excerpt: ''
  });
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    tech: '',
    githubUrl: '',
    liveUrl: ''
  });
  // Per-collection load status: 'loading' | 'slow' | 'ready' | 'error'
  const [reviewsStatus, setReviewsStatus] = useState('loading');
  const [projectsStatus, setProjectsStatus] = useState('loading');
  const [imagesStatus, setImagesStatus] = useState('loading');

  // Listen to Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = observeAuthState((user) => {
      if (user) {
        setCurrentUser(user);
        setIsAuthenticated(true);
        console.log('✅ User logged in:', user.email);
        // Load subscribers when admin logs in
        loadSubscribers();
      } else {
        setCurrentUser(null);
        setIsAuthenticated(false);
        setSubscribers([]);
        console.log('User logged out');
      }
    });

    return () => unsubscribe();
  }, []);

  const loadReviews = useCallback(
    () =>
      runLoad({
        label: 'reviews',
        fetcher: getReviews,
        pick: (r) => r.reviews,
        setData: setReviews,
        setStatus: setReviewsStatus,
      }),
    []
  );

  const loadProjects = useCallback(
    () =>
      runLoad({
        label: 'projects',
        fetcher: getProjects,
        pick: (r) => r.projects,
        setData: setProjects,
        setStatus: setProjectsStatus,
      }),
    []
  );

  const loadImages = useCallback(
    () =>
      runLoad({
        label: 'photos',
        fetcher: getImages,
        pick: (r) => r.images,
        setData: setImages,
        setStatus: setImagesStatus,
      }),
    []
  );

  // Switching pages starts at the top, like following a link would.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  // Load public content on mount
  useEffect(() => {
    loadReviews();
    loadProjects();
    loadImages();
  }, [loadReviews, loadProjects, loadImages]);

  // Load subscribers from Firestore (admin only)
  const loadSubscribers = async () => {
    try {
      const result = await getSubscribers();
      if (result.success) {
        setSubscribers(result.subscribers);
        console.log('✅ Loaded subscribers:', result.subscribers.length);
      } else {
        console.error('❌ Error loading subscribers:', result.error);
      }
    } catch (err) {
      console.error('❌ Error:', err);
    }
  };

  const handleLogin = () => {
    setShowAdminModal(false);
    return true;
  };

  const handleLogout = async () => {
    const result = await signOutUser();
    if (result.success) {
      setIsAuthenticated(false);
      setCurrentUser(null);
      setSubscribers([]);
      setCurrentPage('home');
    }
  };

  const handleAddSubscriber = async (email) => {
    try {
      const result = await addSubscriber(email);
      if (result.success) {
        console.log('✅ Subscriber added successfully');
        // Reload subscribers if admin is logged in
        if (isAuthenticated) {
          loadSubscribers();
        }
        return { success: true };
      } else {
        console.error('❌ Error adding subscriber:', result.error);
        return { success: false, error: result.error };
      }
    } catch (err) {
      console.error('❌ Error:', err);
      return { success: false, error: err.message };
    }
  };

  const handleFileUpload = async(imageData) => {
    try{
      console.log(imageData)
      const result = await saveImageData(imageData);
      if (result.success) {
        console.log('✅ Photo added successfully');
        await loadImages(); // Refresh so the gallery shows the real Storage URL
        return { success: true };
      } else {
        console.error('❌ Error adding photo:', result.error);
        return { success: false, error: result.error };
      }

    } catch(error){
      console.error(error);
      return { success: false, error: error.message };
    }
  }

  const handleDeleteImage = async (photoId, imageUrl, thumbUrl) => {
    try {
      const result = await deleteImage(photoId, imageUrl, thumbUrl);
      if (result.success) {
        console.log('✅ Photo deleted successfully');
        await loadImages();
      } else {
        alert('Failed to delete photo: ' + result.error);
      }
      return result;
    } catch (err) {
      console.error('❌ Error:', err);
      return { success: false, error: err.message };
    }
  }

  const handleAddReview = async (reviewData) => {
    console.log('🚀 Starting to add/update review...', reviewData);
    
    try {
      if (editingReview) {
        // Update existing review
        console.log('📝 Updating review ID:', editingReview.id);
        const result = await updateReview(editingReview.id, reviewData);
        
        if (result.success) {
          console.log('✅ Review updated successfully');
          await loadReviews();
          setShowReviewModal(false);
          setEditingReview(null);
        } else {
          console.error('❌ Update failed:', result.error);
          alert('Failed to update review: ' + result.error);
        }
      } else {
        // Add new review
        console.log('➕ Adding new review');
        const result = await addReview(reviewData);
        
        if (result.success) {
          console.log('✅ Review added successfully with ID:', result.id);
          await loadReviews();
          setShowReviewModal(false);
          
          // Notify subscribers about the new review
          console.log('📧 Notifying subscribers...');
          try {
            const { notifySubscribersNewReview } = await import('./firebase/functions');
            const notifyResult = await notifySubscribersNewReview({
              title: reviewData.title,
              excerpt: reviewData.excerpt,
              rating: reviewData.rating
            });
            
            if (notifyResult.success) {
              console.log('✅ Subscribers notified:', notifyResult.data);
              alert('Review published and subscribers notified! 📧');
            } else {
              console.error('⚠️ Failed to notify subscribers:', notifyResult.error);
              alert('Review published, but failed to notify subscribers.');
            }
          } catch (notifyError) {
            console.error('⚠️ Error notifying subscribers:', notifyError);
            alert('Review published successfully!');
          }
        } else {
          console.error('❌ Add failed:', result.error);
          alert('Failed to add review: ' + result.error);
        }
      }
    } catch (err) {
      console.error('❌ Unexpected error:', err);
      console.error('Error stack:', err.stack);
      alert('An error occurred: ' + err.message);
    }
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setNewReview({
      title: review.title,
      rating: review.rating,
      content: review.content,
      excerpt: review.excerpt
    });
    setShowReviewModal(true);
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const result = await deleteReview(id);
      if (result.success) {
        console.log('✅ Review deleted successfully');
        await loadReviews(); // Reload all reviews
      } else {
        alert('Failed to delete review: ' + result.error);
      }
    } catch (err) {
      console.error('❌ Error:', err);
      alert('An error occurred while deleting the review');
    }
  };

  const openNewReviewModal = () => {
    setEditingReview(null);
    setNewReview({ title: '', rating: 5, content: '', excerpt: '' });
    setShowReviewModal(true);
  };

  const handleAddProject = async (projectData) => {
    try {
      if (editingProject) {
        const result = await updateProject(editingProject.id, projectData);
        if (result.success) {
          console.log('✅ Project updated successfully');
          await loadProjects();
          setShowProjectModal(false);
          setEditingProject(null);
        } else {
          alert('Failed to update project: ' + result.error);
        }
      } else {
        const result = await addProject(projectData);
        if (result.success) {
          console.log('✅ Project added successfully');
          await loadProjects();
          setShowProjectModal(false);
        } else {
          alert('Failed to add project: ' + result.error);
        }
      }
    } catch (err) {
      console.error('❌ Error:', err);
      alert('An error occurred: ' + err.message);
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setNewProject({
      title: project.title,
      description: project.description,
      tech: toTechList(project.tech).join(', '),
      githubUrl: project.githubUrl || '',
      liveUrl: project.liveUrl || ''
    });
    setShowProjectModal(true);
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      const result = await deleteProject(id);
      if (result.success) {
        console.log('✅ Project deleted successfully');
        await loadProjects();
      } else {
        alert('Failed to delete project: ' + result.error);
      }
    } catch (err) {
      console.error('❌ Error:', err);
      alert('An error occurred while deleting the project');
    }
  };

  const openNewProjectModal = () => {
    setEditingProject(null);
    setNewProject({ title: '', description: '', tech: '', githubUrl: '', liveUrl: '' });
    setShowProjectModal(true);
  };

  // Full pages wait for their own collection only; the rest of the site stays usable.
  const renderWhenLoaded = (status, label, onRetry, render) => {
    if (isPending(status)) {
      return (
        <div className="page py-12">
          <LoadingNotice label={label} slow={status === 'slow'} onRetry={onRetry} />
        </div>
      );
    }
    if (status === 'error') {
      return (
        <div className="page py-12">
          <LoadError label={label} onRetry={onRetry} />
        </div>
      );
    }
    return render();
  };

  return (
    <div className="flex min-h-[100dvh] flex-col">
      <Navigation 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage}
        setShowAdminModal={setShowAdminModal}
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        currentUser={currentUser}
      />
      
      <main className="flex-1">
        {currentPage === 'home' && (
          <ErrorBoundary key="home">
            <HomePage
              reviews={reviews}
              projects={projects}
              subscribers={subscribers}
              images={images}
              status={{ reviews: reviewsStatus, projects: projectsStatus, images: imagesStatus }}
              onRetry={{ reviews: loadReviews, projects: loadProjects, images: loadImages }}
              setCurrentPage={setCurrentPage}
              onAddSubscriber={handleAddSubscriber}
              isAuthenticated={isAuthenticated}
            />
          </ErrorBoundary>
        )}

        {currentPage === 'reviews' && (
          <ErrorBoundary key="reviews">
            {renderWhenLoaded(reviewsStatus, 'reviews', loadReviews, () => (
              <ReviewsPage
                reviews={reviews}
                isAuthenticated={isAuthenticated}
                onEditReview={handleEditReview}
                onDeleteReview={handleDeleteReview}
                onOpenNewReview={openNewReviewModal}
              />
            ))}
          </ErrorBoundary>
        )}

        {currentPage === 'projects' && (
          <ErrorBoundary key="projects">
            {renderWhenLoaded(projectsStatus, 'projects', loadProjects, () => (
              <ProjectsPage
                projects={projects}
                isAuthenticated={isAuthenticated}
                onEditProject={handleEditProject}
                onDeleteProject={handleDeleteProject}
                onOpenNewProject={openNewProjectModal}
              />
            ))}
          </ErrorBoundary>
        )}

        {currentPage === 'gallery' && (
          <ErrorBoundary key="gallery">
            {renderWhenLoaded(imagesStatus, 'photos', loadImages, () => (
              <GalleryPage isAuthenticated={isAuthenticated} onSave={handleFileUpload} onDelete={handleDeleteImage} images={images} />
            ))}
          </ErrorBoundary>
        )}
      </main>

      <Footer />

      <AdminModal 
        showModal={showAdminModal}
        setShowModal={setShowAdminModal}
        onLogin={handleLogin}
      />
      
      {isAuthenticated && (
        <ReviewModal
          showModal={showReviewModal}
          setShowModal={setShowReviewModal}
          review={newReview}
          setReview={setNewReview}
          editingReview={editingReview}
          onSave={handleAddReview}
        />
      )}

      {isAuthenticated && (
        <ProjectModal
          showModal={showProjectModal}
          setShowModal={setShowProjectModal}
          project={newProject}
          setProject={setNewProject}
          editingProject={editingProject}
          onSave={handleAddProject}
        />
      )}
    </div>
  );
};

export default App;